// Copyright 2026 Mo-Sys Engineering Ltd. All Rights Reserved.

import { addToArray, updateArrayItemByKey, removeArrayItemByKey } from '../utils/utils';

const isCurrentShow = (state, showUUID) => state.show?.show_uuid === showUUID;

const isCurrentBlock = (state, blockUUID) => state.block?.block_uuid === blockUUID;

/**
 * =====================
 * Show
 * =====================
 */
export const createShowState = (state, show) => {
  addToArray(state.shows, show);
};

export const updateShowState = (state, updatedShow) => {
  state.shows = updateArrayItemByKey(state.shows, 'show_uuid', updatedShow);
  if (isCurrentShow(state, updatedShow.show_uuid)) {
    state.show = { ...state.show, ...updatedShow };
  }
};

export const deleteShowState = (state, showUuid) => {
  state.shows = state.shows.filter(show => show.show_uuid !== showUuid);
  if (isCurrentShow(state, showUuid)) {
    state.show = null;
  }
};

/**
 * =====================
 * Show Scenes
 * =====================
 */
export const addShowSceneState = (state, newScene) => {
  const { show_uuid: newSceneShowUuid } = newScene;

  if (state.show?.show_uuid !== newSceneShowUuid) {
    return;
  }
  // check if scene is already in showScenes
  const existingScene = state.showScenes.find(
    scene => scene.show_scene_uuid === newScene.show_scene_uuid
  );
  // if not, add it
  if (!existingScene) {
    state.showScenes = [newScene, ...state.showScenes];
  }
};

export const deleteShowSceneState = (state, sceneUuid) => {
  state.showScenes = removeArrayItemByKey(state.showScenes, 'show_scene_uuid', sceneUuid);
};

/**
 * =====================
 * Show Blocks
 * =====================
 */
export const createShowBlockState = (state, newBlock) => {
  if (isCurrentShow(state, newBlock.show_uuid) && !state.show.show_blocks.length) {
    addToArray(state.show.show_blocks, newBlock);
  }
};

export const updateShowBlockState = (state, updatedBlock) => {
  state.show.show_blocks = updateArrayItemByKey(state.show.show_blocks, 'block_uuid', updatedBlock);

  if (isCurrentBlock(state, updatedBlock.block_uuid)) {
    state.block = { ...state.block, ...updatedBlock };
  }
};

export const deleteShowBlockState = (state, blockUuid) => {
  state.show.show_blocks = removeArrayItemByKey(state.show.show_blocks, 'block_uuid', blockUuid);
};

/**
 * =====================
 * Reorder show blocks
 * =====================
 */

export const reorderShowBlocksState = (state, reorderedBlocks) => {
  /**
   * Backend stores block order in steps of 100.
   * This reducer simulates the same logic locally during drag-and-drop
   * so the UI updates instantly without extra re-renders.
   * The final order is later confirmed with the backend.
   */
  const { movedBlock, newIndex } = reorderedBlocks;
  const { block_uuid: blockUuid } = movedBlock;
  if (!isCurrentShow(state, movedBlock.show_uuid)) {
    return;
  }

  const blocks = [...state.show.show_blocks];
  // Find the index of the moved block
  const currentIndex = blocks.findIndex(b => b.block_uuid === blockUuid);
  // Return if block not found or already in the correct position
  if (currentIndex === -1 || newIndex === currentIndex) {
    return;
  }
  // Remove block from its current position and insert it at the new index
  const movedItem = blocks[currentIndex];

  blocks.splice(currentIndex, 1);
  blocks.splice(newIndex, 0, movedItem);
  // Recalculate order values in steps of 100
  blocks.forEach((block, i) => {
    if (i === 0) {
      block.order = 100;
    } else {
      const prevOrder = blocks[i - 1].order;
      block.order = prevOrder + 100;
    }
  });
  // Update state with the reordered block list
  state.show.show_blocks = blocks;
};

/**
 * =====================
 * Block script lines
 * =====================
 */
export const createBlockScriptLineState = (state, newLine, subject = null, itemBefore = null) => {
  if (state.block?.block_uuid === newLine?.show_block_uuid) {
    const lines = state.block.lines || [];
    const lineWithSubject = { ...newLine, subject: subject || null };
    const index = itemBefore ? lines.findIndex(l => l.line_uuid === itemBefore) : -1;
    if (index === -1) {
      // Append to the end if no insertAfter or not found
      state.block.lines = [...lines, lineWithSubject];
    } else {
      // Insert right after the matched line
      state.block.lines = [
        ...lines.slice(0, index + 1),
        lineWithSubject,
        ...lines.slice(index + 1),
      ];
    }
  }
};

export const updateBlockScriptLineState = (state, updatedLine) => {
  if (!isCurrentBlock(state, updatedLine?.show_block_uuid)) {
    return;
  }

  state.block.lines = updateArrayItemByKey(state.block.lines, 'line_uuid', updatedLine);
};

export const deleteBlockScriptLineState = (state, lineId, showBlockUuid) => {
  if (!isCurrentBlock(state, showBlockUuid)) {
    return;
  }

  state.block.lines = removeArrayItemByKey(state.block.lines, 'line_uuid', lineId);
};

export const updateBlockScriptLineSubjectState = (state, updatedSubject) => {
  state.block.lines = state.block.lines.map(line => {
    if (line?.subject && line.subject?.subject_uuid === updatedSubject?.subject_uuid) {
      return {
        ...line,
        subject: updatedSubject,
        subject_uuid: updatedSubject.subject_uuid,
      };
    }
    return line;
  });
};

export const updateBlockScriptLineLocationState = (state, updatedLocation) => {
  state.block.lines = state.block.lines.map(line => {
    if (line?.location && line.location?.location_uuid === updatedLocation?.location_uuid) {
      return {
        ...line,
        location: updatedLocation,
        location_uuid: updatedLocation.location_uuid,
      };
    }
    return line;
  });
};

/**
 * =====================
 * Block equipment
 * =====================
 */
export const createBlockEquipmentState = (state, newEquipment) => {
  if (!isCurrentBlock(state, newEquipment?.show_block?.block_uuid)) {
    return;
  }

  state.block = {
    ...state.block,
    ...newEquipment.show_block,
    block_equipment: [...(state.block?.block_equipment || []), newEquipment],
  };
};

export const deleteBlockEquipmentState = (state, equipmentUuid) => {
  state.block.block_equipment = removeArrayItemByKey(
    state.block.block_equipment,
    'blk_equip_uuid',
    equipmentUuid
  );
};

export const updateBlockEquipmentSettingsState = (state, updatedSettings) => {
  if (!isCurrentBlock(state, updatedSettings?.show_block?.block_uuid)) {
    return;
  }
  state.block = {
    ...state.block,
    ...updatedSettings.show_block,
    block_equipment: state.block.block_equipment.map(equip =>
      equip.blk_equip_uuid === updatedSettings.blk_equip_uuid ? updatedSettings : equip
    ),
  };
};

/**
 * =====================
 * Deployments
 * =====================
 */

export const createDeployState = (state, newDeploy) => {
  if (!isCurrentShow(state, newDeploy.show_uuid)) {
    return;
  }
  state.deploy = newDeploy;
};

export const setShowDeployState = (state, showDeployment) => {
  const { deploymentUUID, state: deployed, progressPercentage, message } = showDeployment;
  // if state.deploy.deployment_uuid is same as deploymentUUID, update state.deploy
  if (state.deploy && state.deploy?.deploy_uuid === deploymentUUID) {
    state.deploy = {
      ...state.deploy,
      state: deployed, // "Launching", "Launched", "Failed Launching"
      launch_percent: progressPercentage,
    };
    // message set if state is "Failed Launching"
    state.error.deploy = message && deployed === 'Failed Launching' ? message : null;
  }
};

/**
 * =====================
 * Show members
 * =====================
 */
export const createShowMemberState = (state, newMember) => {
  if (!isCurrentShow(state, newMember.show_uuid)) {
    return;
  }
  addToArray(state.showMembers, newMember);
};

export const deleteShowMemberState = (state, memberUuid) => {
  state.showMembers = removeArrayItemByKey(state.showMembers, 'show_access_uuid', memberUuid);
};
